import React, { createContext, useState, FC, ReactNode, Dispatch, SetStateAction, useEffect } from 'react';

interface CrewType {
  id: number;
  title: string;
  description: string;
  phase?:number
}
interface SpaceType {
  id: number;
  title: string;
  description: string;
  phase?:number
}
interface CrewAndSpaceContextType {
  crews: CrewType[];
  spaces: SpaceType[];
  setCrews: Dispatch<SetStateAction<CrewType[]>>;
  setSpaces: Dispatch<SetStateAction<SpaceType[]>>;
  deleteCrew: (id: number) => void;
  deleteSpace: (id: number) => void;
}
// Create context for managing crews and spaces
export const CrewAndSpaceContext = createContext<CrewAndSpaceContextType | null>(null);
// Custom hook to access the Crew and Space context
export const useCrewAuth = () => {
  return React.useContext(CrewAndSpaceContext);
};

export const CrewAndSpaceProvider: FC<{ children: ReactNode }> = ({ children }) => {
 
  const [crews, setCrews] = useState<CrewType[]>(() => {
    const storedCrews = localStorage.getItem("crews");
    return storedCrews
      ? JSON.parse(storedCrews).map((crew: CrewType) => ({
          ...crew,
          description: crew.description?.trim() ? crew.description : "No description available",
        }))
      : [];
  });
  
  const [spaces, setSpaces] = useState<SpaceType[]>(() => {
    const storedSpaces = localStorage.getItem("spaces");
    return storedSpaces
      ? JSON.parse(storedSpaces).map((space: SpaceType) => ({
          ...space,
          description: space.description?.trim() ? space.description : "No description available",
        }))
      : [];
  });
  
  // Persist the crews state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('crews', JSON.stringify(crews));
  }, [crews]);

  // Persist the spaces state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('spaces', JSON.stringify(spaces));
  }, [spaces]);

  // Function to delete a space and update localStorage
  const deleteCrew = (id: number) => {
    setCrews((prev) => {
      const updatedCrews = prev.filter((crew) => crew.id !== id);
      localStorage.setItem('crews', JSON.stringify(updatedCrews));
      return updatedCrews;
    });
  };


  const deleteSpace = (id: number) => {
    setSpaces((prev) => {
      const updatedSpaces = prev.filter((space) => space.id !== id);
      localStorage.setItem('spaces', JSON.stringify(updatedSpaces));
      return updatedSpaces;
    });
  };

  return (
    <CrewAndSpaceContext.Provider value={{ crews, spaces, setCrews, setSpaces, deleteCrew, deleteSpace }}>
      {children}
    </CrewAndSpaceContext.Provider>
  );
};
